import { useMemo } from 'react';
import { useTenant } from '../../hooks/useTenant';
import { useAuth } from '../../hooks/useAuth';
import { useRealTimeMetrics } from '../../hooks/useRealTimeMetrics';
import { useDateRange } from '../../hooks/useDateRange';

export default function FlowsView() {
  const { user } = useAuth();
  const { selectedTenantId } = useTenant();
  const { days } = useDateRange();
  const { data, isLoading } = useRealTimeMetrics({
    endpoint: 'flows',
    interval: 60000,
    days,
  });

  const nodeColors = [
    '#FF6B35', '#004E89', '#1A659E', '#B8336A', '#F77F00',
    '#06A77D', '#D62828', '#8338EC', '#FFBE0B', '#FB5607',
  ];

  const processedFlowData = useMemo(() => {
    const nodes = data?.nodes;
    const links = data?.links;
    if (!nodes?.length || !links?.length) return null;
    return { nodes, links };
  }, [data]);

  const totalFlows = data?.totalFlows ?? 0;
  const totalSessions = data?.totalSessions ?? 0;

  // Custom SVG Sankey layout — BFS column assignment (supports N columns)
  const sankeyLayout = useMemo(() => {
    if (!processedFlowData?.nodes?.length || !processedFlowData?.links?.length) return null;
    const flowData = processedFlowData;

    const W = 1000, H = 300;
    const nodeW = 16, pad = 16;

    // BFS from entry node (0) to assign depth-based columns
    const col: Record<number, number> = { 0: 0 };
    const queue: number[] = [0];
    while (queue.length) {
      const curr = queue.shift()!;
      flowData.links.forEach((l: any) => {
        if (l.source === curr && col[l.target] === undefined) {
          col[l.target] = col[curr] + 1;
          queue.push(l.target);
        }
      });
    }
    // Keep BFS-derived columns for regular nodes.
    // Exit gets a dedicated final column to avoid overlapping with leaf attraction nodes.
    const hasBFS = Math.max(0, ...Object.values(col));
    const exitIndex = flowData.nodes.findIndex((n: any) => n?.name === 'Exit');
    flowData.nodes.forEach((_: any, i: number) => {
      if (col[i] === undefined) col[i] = 0;
      if (i === exitIndex) {
        col[i] = hasBFS + 1;
      }
    });

    const numCols = Math.max(2, Math.max(...Object.values(col)) + 1);
    const colXArr = Array.from({ length: numCols }, (_, c) =>
      50 + (c * (W - 100 - nodeW)) / (numCols - 1)
    );

    // Group nodes by column
    const colGroups: Record<number, number[]> = {};
    flowData.nodes.forEach((_: any, i: number) => {
      const c = col[i];
      if (!colGroups[c]) colGroups[c] = [];
      colGroups[c].push(i);
    });

    // Per-node flow (max of in/out)
    const nodeInFlow: Record<number, number> = {};
    const nodeOutFlow: Record<number, number> = {};
    flowData.nodes.forEach((_: any, i: number) => { nodeInFlow[i] = 0; nodeOutFlow[i] = 0; });
    flowData.links.forEach((l: any) => {
      nodeOutFlow[l.source] = (nodeOutFlow[l.source] || 0) + l.value;
      nodeInFlow[l.target] = (nodeInFlow[l.target] || 0) + l.value;
    });
    const nodeFlow = (i: number) => Math.max(nodeInFlow[i] || 0, nodeOutFlow[i] || 0);

    // ── Global scale (D3-Sankey style) ──────────────────────────────────────
    // Scale = pixels-per-flow-unit, computed so the tallest column just fits
    // Each column uses natural node heights (not normalized to fill H)
    // then is centered vertically.
    const nodePad = 12;
    const minNodeH = 8;

    let scale = Infinity;
    Object.entries(colGroups).forEach(([, indices]) => {
      const n = indices.length;
      const totalF = indices.reduce((s, i) => s + nodeFlow(i), 0) || 1;
      const availH = H - nodePad * Math.max(n - 1, 0) - minNodeH * n;
      scale = Math.min(scale, availH / totalF);
    });
    if (!isFinite(scale) || scale <= 0) scale = 1;

    // Node positions — global scale, each column centred vertically
    const nodePos: Record<number, { x: number; y: number; h: number; color: string }> = {};
    Object.entries(colGroups).forEach(([cStr, indices]) => {
      const c = parseInt(cStr);
      const heights = indices.map((i) => Math.max(Math.round(nodeFlow(i) * scale), minNodeH));
      const colH = heights.reduce((s, h) => s + h, 0) + nodePad * Math.max(indices.length - 1, 0);
      let y = Math.max(pad, (H - colH) / 2); // centre vertically
      indices.forEach((i, ci) => {
        nodePos[i] = { x: colXArr[c], y, h: heights[ci], color: nodeColors[i % nodeColors.length] };
        y += heights[ci] + nodePad;
      });
    });

    // Build ribbon paths
    const paths = flowData.links.map((l: any, li: number) => {
      const src = nodePos[l.source];
      const tgt = nodePos[l.target];
      if (!src || !tgt) return null;
      const totalSrc = flowData.links.filter((x: any) => x.source === l.source).reduce((s: number, x: any) => s + x.value, 0) || 1;
      const totalTgt = flowData.links.filter((x: any) => x.target === l.target).reduce((s: number, x: any) => s + x.value, 0) || 1;
      const lh = Math.max(2, Math.round((l.value / totalSrc) * src.h));
      const rh = Math.max(2, Math.round((l.value / totalTgt) * tgt.h));

      const srcLinks = flowData.links.filter((x: any) => x.source === l.source);
      const tgtLinks = flowData.links.filter((x: any) => x.target === l.target);
      const srcOff = srcLinks.slice(0, srcLinks.findIndex((x: any) => x === l)).reduce((s: number, x: any) => s + Math.max(2, Math.round((x.value / totalSrc) * src.h)), 0);
      const tgtOff = tgtLinks.slice(0, tgtLinks.findIndex((x: any) => x === l)).reduce((s: number, x: any) => s + Math.max(2, Math.round((x.value / totalTgt) * tgt.h)), 0);

      const x0 = src.x + nodeW, y0 = src.y + srcOff + lh / 2;
      const x1 = tgt.x, y1 = tgt.y + tgtOff + rh / 2;
      const cx = (x0 + x1) / 2;

      return (
        <path
          key={li}
          d={`M${x0},${y0 - lh / 2} C${cx},${y0 - lh / 2} ${cx},${y1 - rh / 2} ${x1},${y1 - rh / 2}
             L${x1},${y1 + rh / 2} C${cx},${y1 + rh / 2} ${cx},${y0 + lh / 2} ${x0},${y0 + lh / 2} Z`}
          fill={src.color}
          fillOpacity={0.35}
        />
      );
    }).filter(Boolean);

    // Label card width
    const cardW = (name: string) => Math.max(90, name.length * 7.5 + 20);
    const entryFlow = nodeOutFlow[0] || 1;

    const rects = flowData.nodes.map((n: any, i: number) => {
      const p = nodePos[i];
      if (!p) return null;
      const c = col[i];
      const isExitNode = i === exitIndex;
      const isRight = c === numCols - 1;
      const name = typeof n.name === 'string' ? n.name : n.name?.it || n.name?.en || 'Unknown';
      const cw = cardW(name);
      // Keep cards away from the dedicated Exit lane:
      // nodes in the last/penultimate columns render labels on the left.
      const preferLeftCard = isExitNode || (c >= numCols - 2 && c !== 0);
      const rawCardX = preferLeftCard ? p.x - 12 - cw : p.x + nodeW + 12;
      const cardX = Math.max(6, Math.min(rawCardX, W - cw - 6));
      const cardY = p.y + p.h / 2 - 20;
      const flow = nodeFlow(i);
      const pct = Math.min(100, Math.round((flow / entryFlow) * 100));
      return (
        <g key={i}>
          <rect x={p.x} y={p.y} width={nodeW} height={p.h} fill={p.color} rx={3} />
          <rect x={cardX} y={cardY} width={cw} height={40} fill="white" rx={6} opacity={0.95} filter="url(#cardShadow)" />
          <text x={cardX + 10} y={cardY + 14} fontSize={11} fontWeight={700} fill="#1e293b">{name}</text>
          <text x={cardX + 10} y={cardY + 30} fontSize={11} fontWeight={600} fill={p.color}>
            {flow} · {pct}%
          </text>
        </g>
      );
    }).filter(Boolean);

    return { paths, rects, W, H };
  }, [processedFlowData]);

  return (
    <div>
      <div className="mb-6">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-bold text-slate-900 m-0">Visitor Flows</h1>
        </div>
        <p className="text-sm text-slate-500 mt-1">
          Track visitor movement through attractions
          {totalSessions > 0 && (
            <span className="ml-2 text-slate-400">· {totalFlows} flows from {totalSessions} sessions</span>
          )}
        </p>
      </div>

      {user?.role === 'superadmin' && !selectedTenantId && (
        <div className="p-6 bg-red-50 rounded-xl border border-red-300 text-red-900 text-sm">
          Please select a tenant from the dropdown above to view data.
        </div>
      )}

      {isLoading && !processedFlowData ? (
        <div className="p-8 text-center">
          <div className="inline-block w-8 h-8 border-2 border-slate-200 border-t-orange-600 rounded-full animate-spin" />
          <p className="mt-4 text-slate-600">Loading flow data...</p>
        </div>
      ) : sankeyLayout ? (
        <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm">
          <svg
            viewBox={`0 0 ${sankeyLayout.W} ${sankeyLayout.H}`}
            width="100%"
            className="min-h-48"
          >
            <defs>
              <filter id="cardShadow" x="-20%" y="-30%" width="140%" height="160%">
                <feDropShadow dx="0" dy="2" stdDeviation="4" floodColor="#000" floodOpacity="0.10" />
              </filter>
            </defs>
            {/* Background */}
            <rect x={0} y={0} width={sankeyLayout.W} height={sankeyLayout.H} fill="#f0f5fb" rx={10} />
            {sankeyLayout.paths}
            {sankeyLayout.rects}
          </svg>
          <p className="text-xs text-slate-500 mt-4 border-t pt-4">
            ↳ The diagram shows visitor flows through your top attractions. Wider connections indicate more visitors.
            {isLoading && <span className="ml-2 text-orange-500">⟳ Updating...</span>}
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-xl p-8 border border-slate-200 flex flex-col items-center justify-center min-h-72">
          <svg className="w-16 h-16 text-slate-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 12a5 5 0 1010 0A5 5 0 007 12z" />
          </svg>
           <span className="text-slate-600 text-sm font-medium">Nessun dato per questo periodo</span>
           <span className="text-slate-400 text-xs mt-1">Le visite verranno visualizzate man mano che i visitatori scansionano i QR code</span>
        </div>
      )}
    </div>
  );
}
